import React, { useState } from "react";

const CAMPAIGN_TYPES = [
  { id: "1", name: "Equity", description: "Investors receive ownership shares" },
  { id: "2", name: "Debt", description: "Investors receive interest payments" },
  { id: "3", name: "Fund", description: "Investors access a managed fund strategy" },
];

const CAMPAIGN_ACCESS_OPTIONS = [
  { value: "Start Up", label: "Public (Start Up)" },
  { value: "Offering Memorandum", label: "Offering Memorandum" },
  { value: "Accredited Investor", label: "Deal Room (Accredited Investor)" },
];

const RISK_PROFILE_OPTIONS = [
  { value: "LOW", label: "Low" },
  { value: "LOW_MODERATE", label: "Low-Moderate" },
  { value: "MODERATE", label: "Moderate" },
  { value: "MODERATE_HIGH", label: "Moderate-High" },
  { value: "HIGH", label: "High" },
];

const Step2CampaignDetails = ({ formData, updateFormData, nextStep, prevStep }) => {
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    const nextData = { [field]: value };

    if (field === "minimum_investment") {
      const minimumValue = parseFloat(value);
      if (!Number.isNaN(minimumValue) && minimumValue > 25000) {
        nextData.campaign_for = "Accredited Investor";
      }
    }

    updateFormData(nextData);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleCampaignTypeChange = (typeId) => {
    updateFormData({
      campaign_type: typeId,
      // Clear type-specific fields when switching
      equity_percentage: typeId === "1" ? formData.equity_percentage : "",
      interest_rate: typeId === "2" ? formData.interest_rate : "",
      maturity_months: typeId === "2" ? formData.maturity_months : "",
      fund_type_description: typeId === "3" ? formData.fund_type_description : "",
      one_line_description: typeId === "3" ? formData.one_line_description : "",
      risk_profile: typeId === "3" ? formData.risk_profile : "",
      investment_horizon: typeId === "3" ? formData.investment_horizon : "",
      target_arr: typeId === "3" ? formData.target_arr : "",
      fund_summary: typeId === "3" ? formData.fund_summary : "",
      key_reasons_to_invest: typeId === "3" ? formData.key_reasons_to_invest : "",
      company_overview: typeId === "3" ? formData.company_overview : "",
    });
  };

  const validateAndNext = () => {
    const newErrors = {};

    if (!formData.campaign_title?.trim()) {
      newErrors.campaign_title = "Campaign title is required";
    } else if (formData.campaign_title.length < 5) {
      newErrors.campaign_title = "Campaign title must be at least 5 characters";
    }

    if (formData.campaign_type !== "3") {
      if (!formData.campaign_description?.trim()) {
        newErrors.campaign_description = "Campaign description is required";
      } else if (formData.campaign_description.length < 50) {
        newErrors.campaign_description = "Description must be at least 50 characters";
      }
    }

    if (formData.campaign_type !== "3") {
      if (!formData.raise_goal || parseFloat(formData.raise_goal) <= 0) {
        newErrors.raise_goal = "Raise goal must be a positive number";
      }
    } else if (formData.raise_goal && parseFloat(formData.raise_goal) <= 0) {
      newErrors.raise_goal = "Raise goal must be a positive number";
    }

    if (!formData.minimum_investment || parseFloat(formData.minimum_investment) <= 0) {
      newErrors.minimum_investment = "Minimum investment must be a positive number";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (!formData.end_date) {
      newErrors.end_date = "End date is required";
    }

    // Validate date range
    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate <= startDate) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    // Validate type-specific fields
    if (formData.campaign_type === "1") {
      if (!formData.equity_percentage || parseFloat(formData.equity_percentage) <= 0) {
        newErrors.equity_percentage = "Equity percentage is required for equity campaigns";
      } else if (parseFloat(formData.equity_percentage) > 100) {
        newErrors.equity_percentage = "Equity percentage cannot exceed 100%";
      }
    } else if (formData.campaign_type === "2") {
      if (!formData.interest_rate || parseFloat(formData.interest_rate) <= 0) {
        newErrors.interest_rate = "Interest rate is required for debt campaigns";
      }
      if (!formData.maturity_months || parseInt(formData.maturity_months) <= 0) {
        newErrors.maturity_months = "Maturity period is required for debt campaigns";
      }
    } else if (formData.campaign_type === "3") {
      if (!formData.one_line_description?.trim()) {
        newErrors.one_line_description = "One-line description is required for funds";
      }
      if (!formData.risk_profile) {
        newErrors.risk_profile = "Risk profile is required for funds";
      }
    }

    if (!formData.campaign_for) {
      newErrors.campaign_for = "Campaign access is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      nextStep();
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="step-form">
      <div className="form-section">
        <h3 className="section-title">Campaign Details</h3>

        <div className="form-group">
          <label>
            {formData.campaign_type === "3" ? "Fund Name" : "Campaign Title"}{" "}
            <span className="required">*</span>
          </label>
          <input
            type="text"
            placeholder={
              formData.campaign_type === "3"
                ? "Enter the legal fund name"
                : "Enter a compelling campaign title"
            }
            value={formData.campaign_title}
            onChange={(e) => handleInputChange("campaign_title", e.target.value)}
            maxLength={100}
          />
          {errors.campaign_title && <div className="error-message">{errors.campaign_title}</div>}
          <div className="help-text">{formData.campaign_title?.length || 0}/100 characters</div>
        </div>

        <div className="form-group">
          <label>
            {formData.campaign_type === "3" ? "Fund Summary" : "Campaign Description"}{" "}
            {formData.campaign_type !== "3" && <span className="required">*</span>}
          </label>
          <textarea
            placeholder={
              formData.campaign_type === "3"
                ? "Describe the fund strategy and objectives..."
                : "Describe the campaign, business opportunity, and investment details..."
            }
            value={formData.campaign_description}
            onChange={(e) => handleInputChange("campaign_description", e.target.value)}
            rows={6}
          />
          {errors.campaign_description && (
            <div className="error-message">{errors.campaign_description}</div>
          )}
          <div className="help-text">
            {formData.campaign_description?.length || 0} characters
            {formData.campaign_type !== "3" ? " (minimum 50)" : ""}
          </div>
        </div>

        <div className="form-group">
          <label>
            Campaign Type <span className="required">*</span>
          </label>
          <div className="campaign-type-toggle">
            {CAMPAIGN_TYPES.map((type) => (
              <div
                key={type.id}
                className={`type-option ${formData.campaign_type === type.id ? "selected" : ""}`}
                onClick={() => handleCampaignTypeChange(type.id)}
              >
                <div className="type-title">{type.name}</div>
                <div className="type-description">{type.description}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>
            Campaign Access <span className="required">*</span>
          </label>
          <select
            value={formData.campaign_for || ""}
            onChange={(e) => handleInputChange("campaign_for", e.target.value)}
          >
            <option value="" disabled>
              Select access type
            </option>
            {CAMPAIGN_ACCESS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.campaign_for && (
            <div className="error-message">{errors.campaign_for}</div>
          )}
          <div className="help-text">
            Deal Room campaigns are restricted to accredited investors. Minimum investment above
            $25k will default to Deal Room access.
          </div>
        </div>

        {/* Equity-specific fields */}
        {formData.campaign_type === "1" && (
          <div className="form-row">
            <div className="form-group">
              <label>
                Equity Percentage Offered <span className="required">*</span>
              </label>
              <input
                type="number"
                placeholder="e.g., 10"
                value={formData.equity_percentage}
                onChange={(e) => handleInputChange("equity_percentage", e.target.value)}
                min="0.01"
                max="100"
                step="0.01"
              />
              {errors.equity_percentage && (
                <div className="error-message">{errors.equity_percentage}</div>
              )}
              <div className="help-text">Percentage of equity offered to investors</div>
            </div>
          </div>
        )}

        {/* Debt-specific fields */}
        {formData.campaign_type === "2" && (
          <div className="form-row">
            <div className="form-group">
              <label>
                Interest Rate (% per annum) <span className="required">*</span>
              </label>
              <input
                type="number"
                placeholder="e.g., 8.5"
                value={formData.interest_rate}
                onChange={(e) => handleInputChange("interest_rate", e.target.value)}
                min="0.01"
                step="0.01"
              />
              {errors.interest_rate && <div className="error-message">{errors.interest_rate}</div>}
            </div>
            <div className="form-group">
              <label>
                Maturity Period (months) <span className="required">*</span>
              </label>
              <input
                type="number"
                placeholder="e.g., 24"
                value={formData.maturity_months}
                onChange={(e) => handleInputChange("maturity_months", e.target.value)}
                min="1"
              />
              {errors.maturity_months && (
                <div className="error-message">{errors.maturity_months}</div>
              )}
            </div>
          </div>
        )}

        {/* Fund-specific fields */}
        {formData.campaign_type === "3" && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Fund Type Description</label>
                <input
                  type="text"
                  placeholder="e.g., Open-End Real Estate Fund"
                  value={formData.fund_type_description}
                  onChange={(e) => handleInputChange("fund_type_description", e.target.value)}
                  maxLength={255}
                />
              </div>
              <div className="form-group">
                <label>
                  One-Line Description <span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Short description for card display"
                  value={formData.one_line_description}
                  onChange={(e) => handleInputChange("one_line_description", e.target.value)}
                  maxLength={500}
                />
                {errors.one_line_description && (
                  <div className="error-message">{errors.one_line_description}</div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Risk Profile <span className="required">*</span>
                </label>
                <select
                  value={formData.risk_profile || ""}
                  onChange={(e) => handleInputChange("risk_profile", e.target.value)}
                >
                  <option value="" disabled>
                    Select risk level
                  </option>
                  {RISK_PROFILE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.risk_profile && (
                  <div className="error-message">{errors.risk_profile}</div>
                )}
              </div>
              <div className="form-group">
                <label>Investment Horizon</label>
                <input
                  type="text"
                  placeholder="e.g., 3-5 years"
                  value={formData.investment_horizon}
                  onChange={(e) => handleInputChange("investment_horizon", e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Target ARR / Expected Returns</label>
                <input
                  type="text"
                  placeholder="e.g., 8-12% annually"
                  value={formData.target_arr}
                  onChange={(e) => handleInputChange("target_arr", e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Key Reasons to Invest</label>
              <textarea
                placeholder="Clear value propositions and differentiators..."
                value={formData.key_reasons_to_invest}
                onChange={(e) => handleInputChange("key_reasons_to_invest", e.target.value)}
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Company Overview</label>
              <textarea
                placeholder="Background of the fund manager or issuing entity..."
                value={formData.company_overview}
                onChange={(e) => handleInputChange("company_overview", e.target.value)}
                rows={4}
              />
            </div>
          </>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>
              Raise Goal ($) {formData.campaign_type !== "3" && <span className="required">*</span>}
            </label>
            <input
              type="number"
              placeholder="e.g., 500000"
              value={formData.raise_goal}
              onChange={(e) => handleInputChange("raise_goal", e.target.value)}
              min="1"
            />
            {errors.raise_goal && <div className="error-message">{errors.raise_goal}</div>}
            <div className="help-text">
              {formData.campaign_type === "3"
                ? "Optional total offering for the fund"
                : "Total amount to be raised"}
            </div>
          </div>

          <div className="form-group">
            <label>
              Minimum Investment ($) <span className="required">*</span>
            </label>
            <input
              type="number"
              placeholder="e.g., 1000"
              value={formData.minimum_investment}
              onChange={(e) => handleInputChange("minimum_investment", e.target.value)}
              min="1"
            />
            {errors.minimum_investment && (
              <div className="error-message">{errors.minimum_investment}</div>
            )}
            <div className="help-text">
              {parseFloat(formData.minimum_investment) > 25000
                ? "Accredited investors only (min > $25,000)"
                : "Open to all investors"}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              Campaign Start Date <span className="required">*</span>
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => handleInputChange("start_date", e.target.value)}
              min={today}
            />
            {errors.start_date && <div className="error-message">{errors.start_date}</div>}
          </div>

          <div className="form-group">
            <label>
              Campaign End Date <span className="required">*</span>
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => handleInputChange("end_date", e.target.value)}
              min={formData.start_date || today}
            />
            {errors.end_date && <div className="error-message">{errors.end_date}</div>}
          </div>
        </div>
      </div>

      <div className="step-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          Back
        </button>
        <button className="btn btn-primary" onClick={validateAndNext}>
          Next: Financial Info
        </button>
      </div>
    </div>
  );
};

export default Step2CampaignDetails;
